import BaseEndpoint from "../endpoints/baseEndpoint";
import useAuthUser from "../hooks/useAuthUser";

const parseTimer = (raw) => {
    if (!raw) {
        return { instruksi: 0, ujian: 0, inkolom: 0, changekolom: 0 };
    }

    let value = raw;
    while (typeof value === "string") {
        try {
            const parsed = JSON.parse(value);
            value = parsed;
        } catch (error) {
            break;
        }
    }

    if (!value || typeof value !== "object") {
        return { instruksi: 0, ujian: 0, inkolom: 0, changekolom: 0 };
    }

    return {
        instruksi: Number(value?.instruksi ?? value?.instruction ?? 0),
        ujian: Number(value?.ujian ?? value?.exam ?? value?.duration ?? 0),
        inkolom: Number(value?.inkolom ?? 0),
        changekolom: Number(value?.changekolom ?? 0),
    };
};
const parseId = (value) => (value === undefined || value === null ? '' : String(value));

export default async function getSpecialPotentialTest(version, id) {
    let token = "";
    try {
        const raw = JSON.parse(localStorage.getItem('authUser'));
        token = raw?.token || raw?.user?.token || "";
    } catch (e) {}

    const queryParts = [];
    if (id && id !== 'null' && id !== 'undefined') {
        queryParts.push(`id=${encodeURIComponent(id)}`);
    }
    if (token) {
        queryParts.push(`token=${encodeURIComponent(token)}`);
    }
    const queryString = queryParts.length > 0 ? `?${queryParts.join('&')}` : '';

    try {
        const sessionResponse = await BaseEndpoint.instance().get(
            `/exam/${version}/instruction${queryString}`
        );
        const examResponse = await BaseEndpoint.instance().get(
            `/exam/${version}${queryString}`
        );

        let packages = [];
        if (Array.isArray(sessionResponse.data)) {
            packages = sessionResponse.data;
        } else if (sessionResponse.data && Array.isArray(sessionResponse.data.data)) {
            packages = sessionResponse.data.data;
        }

        let examPackagesArray = [];
        if (Array.isArray(examResponse.data)) {
            examPackagesArray = examResponse.data;
        } else if (examResponse.data && Array.isArray(examResponse.data.data)) {
            examPackagesArray = examResponse.data.data;
        }
        const examPackages = new Map(
            examPackagesArray.map((pkg) => [String(pkg.id ?? ""), pkg])
        );
        const allPsychotests = examPackagesArray.flatMap((pkg) =>
            Array.isArray(pkg.psychotests) ? pkg.psychotests : []
        );

        const sessions = [];

        packages.forEach((pkg) => {
            const packageTimer = parseTimer(pkg.timer);
            const psychotests = Array.isArray(pkg.psychotests)
                ? pkg.psychotests.filter((pt) => pt.is_active !== false)
                : [];

            if (pkg.instruksi) {
                sessions.push({
                    name: pkg.nama_paket,
                    type: "instruction",
                    content: pkg.instruksi,
                    duration: packageTimer.instruksi * 1000, // Timer is in seconds
                });
            }

            const examPackage = examPackages.get(String(pkg.id ?? ''));

            psychotests.forEach((psychotest) => {
                const psychotestTimer = parseTimer(psychotest.timer);
                const sessionName = `${pkg.nama_paket} - ${psychotest.name}`;

                if (psychotest.instruksi) {
                    sessions.push({
                        name: sessionName,
                        type: "instruction",
                        content: psychotest.instruksi,
                        duration: (psychotestTimer.instruksi || packageTimer.instruksi) * 1000, // Timer is in seconds
                    });
                }

                const examPsychotest =
                    examPackage?.psychotests?.find(
                        (item) =>
                            String(item.id ?? "") ===
                            String(psychotest.id ?? "")
                    ) ||
                    allPsychotests.find(
                        (item) =>
                            String(item.id ?? "") ===
                            String(psychotest.id ?? "")
                    );

                const questions = Array.isArray(examPsychotest?.questions)
                    ? examPsychotest.questions.map((q) => {
                        const questionWithType = { ...q };
                        // Ensure psychotest_type is passed to each question
                        if (!questionWithType.psychotest_type && examPsychotest?.psychotest_type) {
                            questionWithType.psychotest_type = examPsychotest.psychotest_type;
                        }
                        return questionWithType;
                    })
                    : [];

                // Per-kolom timer mode, Kraeplin, or Potensi Kerja / Kecermatan: group questions by kolom with breaks
                const isKraeplinType = (psychotest.psychotest_type || '').toLowerCase() === 'kraeplin' ||
                    (examPsychotest?.psychotest_type || '').toLowerCase() === 'kraeplin';

                const catStr = String(psychotest.category || examPsychotest?.category || '').toLowerCase().trim();
                const typeStr = String(examPsychotest?.psychotest_type || psychotest.psychotest_type || '').toLowerCase().trim();
                const tplStr = String(psychotest.option_template || examPsychotest?.option_template || '').toLowerCase().trim();

                // Potensi Kerja (Text & Image) is decided ONLY by Kategori / template / resolved type from the server.
                // NEVER by instrument/package name or by question fields (master_type defaults to 'text' for every question,
                // so using it would turn IST / Kecerdasan into Potensi Kerja).
                const isPotensiKerjaType = catStr === 'potensi-kerja' ||
                    tplStr.startsWith('potensi-kerja') ||
                    typeStr === 'potensi-kerja';

                const shouldGroupByKolom = psychotestTimer.inkolom > 0 || isKraeplinType || isPotensiKerjaType;

                if (shouldGroupByKolom) {
                    // Group questions by kolom field
                    const kolomGroups = {};
                    questions.forEach((q) => {
                        const k = q.kolom || '1';
                        if (!kolomGroups[k]) kolomGroups[k] = [];
                        kolomGroups[k].push(q);
                    });

                    // Sort kolom numbers
                    const sortedKoloms = Object.keys(kolomGroups).sort(
                        (a, b) => parseInt(a) - parseInt(b)
                    );

                    const breakDuration = psychotestTimer.changekolom > 0
                        ? psychotestTimer.changekolom * 1000
                        : (isKraeplinType || isPotensiKerjaType ? 5000 : 0);

                    sortedKoloms.forEach((kolom, idx) => {
                        // Add break BETWEEN columns (not before first column)
                        if (idx !== 0 && breakDuration > 0) {
                            sessions.push({
                                name: sessionName,
                                type: "break",
                                duration: breakDuration,
                            });
                        }

                        const group = kolomGroups[kolom];

                        if (isKraeplinType) {
                            const totalKraeplinDuration = psychotestTimer.inkolom > 0
                                ? psychotestTimer.inkolom * 1000
                                : (psychotestTimer.ujian || packageTimer.ujian || 1800) * 1000;

                            sessions.push({
                                name: `${sessionName} - Lembar ${kolom}`,
                                type: "exam",
                                psychotest_id: psychotest.id,
                                psychotest_type: 'kraeplin',
                                category: 'work_attitude',
                                option_template: psychotest.option_template || examPsychotest?.option_template || '',
                                content: group,
                                duration: totalKraeplinDuration,
                                inkolom: psychotestTimer.inkolom,
                                changekolom: psychotestTimer.changekolom,
                            });
                        } else {
                            // Non-Kraeplin per-kolom mode (Potensi Kerja / Kecermatan)
                            const perKolomDuration = psychotestTimer.inkolom > 0
                                ? psychotestTimer.inkolom * 1000
                                : (psychotestTimer.ujian || packageTimer.ujian || 600) * 1000;

                            sessions.push({
                                name: `${sessionName} - Kolom ${kolom}`,
                                type: "exam",
                                psychotest_id: psychotest.id,
                                psychotest_type: isPotensiKerjaType ? 'potensi-kerja' : (psychotest.psychotest_type || 'standard'),
                                category: isPotensiKerjaType ? 'potensi-kerja' : (psychotest.category || 'intelligence'),
                                option_template: psychotest.option_template || examPsychotest?.option_template || '',
                                content: group,
                                duration: perKolomDuration,
                                inkolom: psychotestTimer.inkolom,
                                changekolom: psychotestTimer.changekolom,
                            });
                        }
                    });
                } else {
                    // Standard single-session mode (IST, Likert, SJT, etc.)
                    const examDuration = (psychotestTimer.ujian || packageTimer.ujian || 1800) * 1000;
                    sessions.push({
                        name: sessionName,
                        type: "exam",
                        psychotest_id: psychotest.id,
                        psychotest_type: psychotest.psychotest_type || 'standard',
                        category: psychotest.category || 'intelligence',
                        option_template: psychotest.option_template || '',
                        content: questions,
                        duration: examDuration,
                    });
                }
            });
        });

        return sessions;
    } catch (error) {
        console.error('Error loading special potential test sessions:', error);
        return [];
    }
}
